import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Col, Row } from "reactstrap";
import {
  Empty,
  Tag,
  Select,
  Input,
  InputNumber,
  Checkbox,
  Card as AntCard,
} from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import {
  FileAddOutlined,
  AppstoreOutlined,
  TagOutlined,
  CalculatorOutlined,
  DollarOutlined,
  GiftOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import superAdminService from "../../../api/super-admin/superAdminService";

const InvoiceCreate: React.FC = () => {
  const navigate = useNavigate();

  // Customer
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Customer details
  const [custApps, setCustApps] = useState<any[]>([]);
  const [custBundles, setCustBundles] = useState<any[]>([]);
  const [custLinkedCoupon, setCustLinkedCoupon] = useState<any>(null);
  const [custTaxConfig, setCustTaxConfig] = useState<any>(null);
  const [custBillingConfig, setCustBillingConfig] = useState<any>(null);
  const [custCurrencyCode, setCustCurrencyCode] = useState<string>("");
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Form
  const [selectedAppIds, setSelectedAppIds] = useState<number[]>([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState<number[]>([]);
  const [billingPeriod, setBillingPeriod] = useState("");
  const [discountType, setDiscountType] = useState<string | undefined>(undefined);
  const [discountValue, setDiscountValue] = useState<number | undefined>(undefined);
  const [discountReason, setDiscountReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Advance adjustment
  const [advanceBalance, setAdvanceBalance] = useState<number>(0);
  const [useAdvance, setUseAdvance] = useState(false);
  const [advanceToAdjust, setAdvanceToAdjust] = useState<number | undefined>(undefined);

  // Load customer dropdown on mount
  useEffect(() => {
    setCustomerLoading(true);
    superAdminService.getCustomerDropdown().then((res: any) => {
      if (res.status && res.data) {
        setCustomerOptions(
          res.data.map((c: any) => ({ label: c.partyName || `Customer #${c.partyId}`, value: c.partyId }))
        );
      }
    }).finally(() => setCustomerLoading(false));
  }, []);

  // Fetch customer details when customer changes
  const handleCustomerChange = useCallback(async (id: number) => {
    setCustomerId(id);
    setSelectedAppIds([]);
    setSelectedBundleIds([]);
    setCustApps([]);
    setCustBundles([]);
    setCustLinkedCoupon(null);
    setCustTaxConfig(null);
    setCustBillingConfig(null);
    setCustCurrencyCode("");
    setAdvanceBalance(0);
    setUseAdvance(false);
    setAdvanceToAdjust(undefined);
    if (!id) return;
    setDetailsLoading(true);
    try {
      const [moduleRes, customerRes, taxRes, billingRes, bundleRes, receivableRes] = await Promise.allSettled([
        superAdminService.getModuleAccess(id),
        superAdminService.getCustomerById(id),
        superAdminService.getTaxConfig(id),
        superAdminService.getBillingConfig(id),
        superAdminService.getCustomerBundles(id),
        superAdminService.getCustomerReceivableDetail(id),
      ]);

      // Extract unique apps from module access data
      if (moduleRes.status === "fulfilled" && moduleRes.value.status && moduleRes.value.data) {
        const modules = Array.isArray(moduleRes.value.data) ? moduleRes.value.data : [];
        const activeModules = modules.filter((m: any) => m.isActive && !m.customerBundleId);
        // Group by appId to get unique apps with their price (exclude bundle-assigned apps)
        const appMap = new Map<number, any>();
        for (const m of activeModules) {
          if (m.appId && !appMap.has(m.appId)) {
            appMap.set(m.appId, {
              appId: m.appId,
              appName: m.appName || `Application ${m.appId}`,
              appPrice: Number(m.finalPrice) || Number(m.appPrice) || 0,
              currencyCode: m.currencyCode || "",
            });
          }
        }
        setCustApps(Array.from(appMap.values()));
      }

      if (customerRes.status === "fulfilled" && customerRes.value.status && customerRes.value.data) {
        setCustLinkedCoupon(customerRes.value.data.linkedCoupon || null);
        setCustCurrencyCode(customerRes.value.data.currencyCode || "");
      }
      if (taxRes.status === "fulfilled" && taxRes.value.status && taxRes.value.data)
        setCustTaxConfig(taxRes.value.data);
      if (billingRes.status === "fulfilled" && billingRes.value.status && billingRes.value.data)
        setCustBillingConfig(billingRes.value.data);
      if (bundleRes.status === "fulfilled" && bundleRes.value.status && bundleRes.value.data)
        setCustBundles(Array.isArray(bundleRes.value.data) ? bundleRes.value.data : []);
      if (receivableRes.status === "fulfilled" && receivableRes.value.status && receivableRes.value.data) {
        const advBal = Number(receivableRes.value.data.summary?.advanceBalance) || 0;
        setAdvanceBalance(advBal);
      }
    } catch {}
    setDetailsLoading(false);
  }, []);

  const handleSubmit = async () => {
    if (!customerId || (selectedAppIds.length === 0 && selectedBundleIds.length === 0)) return;
    setSaving(true);
    try {
      const payload: any = { customerId, appIds: selectedAppIds };
      if (selectedBundleIds.length > 0) payload.bundleIds = selectedBundleIds;
      if (billingPeriod.trim()) payload.billingPeriod = billingPeriod.trim();
      if (discountType && discountValue && discountValue > 0) {
        payload.manualDiscountType = discountType;
        payload.manualDiscountValue = discountValue;
        if (discountReason.trim()) payload.discountReason = discountReason.trim();
      }
      if (notes.trim()) payload.notes = notes.trim();
      if (useAdvance && advanceToAdjust && advanceToAdjust > 0) {
        payload.advanceToAdjust = advanceToAdjust;
      }

      const res = await superAdminService.createManualInvoice(payload);
      if (res.status) {
        Swal.fire("Success", "Invoice created successfully", "success");
        navigate("/sa-invoices");
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg || "Failed to create invoice", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Computed breakup ---
  const selectedApps = custApps.filter((a: any) => selectedAppIds.includes(a.appId));
  const selectedBundles = custBundles.filter((b: any) => selectedBundleIds.includes(b.bundleId));

  const appSubtotal = selectedApps.reduce((sum: number, a: any) => sum + (a.appPrice || 0), 0);
  const bundleSubtotal = selectedBundles.reduce((sum: number, b: any) => sum + (Number(b.effectivePrice) || Number(b.bundlePrice) || 0), 0);
  const subtotal = appSubtotal + bundleSubtotal;

  let couponDiscount = 0;
  if (custLinkedCoupon && subtotal > 0) {
    if (custLinkedCoupon.discountType === "Percent") {
      couponDiscount = subtotal * ((custLinkedCoupon.discountPercent || 0) / 100);
    } else {
      couponDiscount = Math.min(Number(custLinkedCoupon.discountValue) || 0, subtotal);
    }
  }

  let manualDiscount = 0;
  if (discountType && discountValue && discountValue > 0) {
    if (discountType === "Percentage") {
      manualDiscount = subtotal * (discountValue / 100);
    } else {
      manualDiscount = discountValue;
    }
  }

  const totalDiscount = manualDiscount > 0 ? manualDiscount : couponDiscount;
  const netBeforeAdvance = Math.max(subtotal - totalDiscount, 0);
  const advanceDeduction = useAdvance && advanceToAdjust ? Math.min(advanceToAdjust, netBeforeAdvance) : 0;
  const taxableAmount = Math.max(netBeforeAdvance - advanceDeduction, 0);
  const taxRate = custTaxConfig?.taxRate ? Number(custTaxConfig.taxRate) : 0;
  const taxAmount = taxableAmount * (taxRate / 100);
  const totalAmount = taxableAmount + taxAmount;
  const fmt = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalItems = selectedAppIds.length + selectedBundleIds.length;

  return (
    <React.Fragment>
      <Head title="Create Manual Invoice" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                <FileAddOutlined style={{ marginRight: 8 }} />
                Create Manual Invoice
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="primary" className="btn-dim" onClick={() => navigate("/sa-invoices")}>
                <Icon name="arrow-left" />
                <span>Back to Invoices</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-4">
            {/* Left Column: Customer + Form */}
            <Col lg="7">
              {/* Customer Selection */}
              <AntCard size="small" style={{ marginBottom: 16 }} title="Customer & Billing">
                <Row className="g-3">
                  <Col md="6">
                    <label className="form-label">Customer <span className="text-danger">*</span></label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select customer"
                      showSearch
                      loading={customerLoading}
                      filterOption={(input, option: any) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      value={customerId}
                      onChange={handleCustomerChange}
                      options={customerOptions}
                      size="large"
                    />
                  </Col>
                  <Col md="6">
                    <label className="form-label">Billing Period</label>
                    <Input
                      value={billingPeriod}
                      onChange={(e) => setBillingPeriod(e.target.value)}
                      placeholder="e.g., Mar 2026, Q1 2026"
                      maxLength={50}
                      size="large"
                    />
                  </Col>
                </Row>
              </AntCard>

              {detailsLoading && (
                <LoadingSpinner tip="Loading customer details..." />
              )}

              {customerId && !detailsLoading && (
                <>
                  {/* Info Cards */}
                  <Row className="g-2 mb-3">
                    <Col md="4">
                      <AntCard size="small" style={{ background: "#f9f9f9", height: "100%" }}>
                        <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase", marginBottom: 4 }}>Billing Config</div>
                        <div style={{ fontSize: 13 }}>
                          <div><strong>Mode:</strong> {custBillingConfig?.billingMode || "-"}</div>
                          <div><strong>Frequency:</strong> {custBillingConfig?.billingFrequency || "-"}</div>
                          <div><strong>Currency:</strong> {custCurrencyCode || "-"}</div>
                        </div>
                      </AntCard>
                    </Col>
                    <Col md="4">
                      <AntCard size="small" style={{ background: custLinkedCoupon ? "#f6ffed" : "#f9f9f9", borderColor: custLinkedCoupon ? "#b7eb8f" : undefined, height: "100%" }}>
                        <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase", marginBottom: 4 }}>
                          <TagOutlined style={{ marginRight: 4 }} />Active Coupon
                        </div>
                        {custLinkedCoupon ? (
                          <div style={{ fontSize: 13 }}>
                            <Tag color="green">{custLinkedCoupon.couponCode}</Tag>
                            <div style={{ marginTop: 4 }}>
                              {custLinkedCoupon.discountType === "Percent"
                                ? `${custLinkedCoupon.discountPercent}% off`
                                : `Flat ${Number(custLinkedCoupon.discountValue).toFixed(2)} off`}
                            </div>
                            <div style={{ fontSize: 11, color: "#8094ae" }}>
                              {moment(custLinkedCoupon.validFrom).format("DD MMM YY")} — {moment(custLinkedCoupon.validTo).format("DD MMM YY")}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: "#8094ae" }}>No active coupon</div>
                        )}
                      </AntCard>
                    </Col>
                    <Col md="4">
                      <AntCard size="small" style={{ background: custTaxConfig?.taxName ? "#fff7e6" : "#f9f9f9", borderColor: custTaxConfig?.taxName ? "#ffd591" : undefined, height: "100%" }}>
                        <div style={{ fontSize: 11, color: "#8094ae", textTransform: "uppercase", marginBottom: 4 }}>
                          <CalculatorOutlined style={{ marginRight: 4 }} />Tax
                        </div>
                        {custTaxConfig?.taxName ? (
                          <div style={{ fontSize: 13 }}>
                            <Tag color="orange">{custTaxConfig.taxName}</Tag>
                            <div style={{ marginTop: 4, fontWeight: 600 }}>{custTaxConfig.taxRate}%</div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: "#8094ae" }}>No tax configured</div>
                        )}
                      </AntCard>
                    </Col>
                  </Row>

                  {custBillingConfig && custBillingConfig.billingMode !== "Manual" && (
                    <div style={{ background: "#fff2e8", border: "1px solid #ffbb96", borderRadius: 6, padding: "10px 16px", marginBottom: 16, fontSize: 13 }}>
                      <strong>Note:</strong> Manual invoice creation is only allowed for <strong>Manual</strong> billing mode. Current: <Tag color="orange">{custBillingConfig.billingMode}</Tag>
                    </div>
                  )}

                  {/* App Selection */}
                  <AntCard size="small" style={{ marginBottom: 16 }} title={<span><AppstoreOutlined style={{ marginRight: 6 }} />Select SaaS Pricing to Invoice <span className="text-danger">*</span></span>}>
                    {custApps.length > 0 ? (
                      <>
                        <div style={{ marginBottom: 8 }}>
                          <Checkbox
                            checked={selectedAppIds.length === custApps.length && custApps.length > 0}
                            indeterminate={selectedAppIds.length > 0 && selectedAppIds.length < custApps.length}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedAppIds(custApps.map((a: any) => a.appId));
                              else setSelectedAppIds([]);
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>Select All ({custApps.length} apps)</span>
                          </Checkbox>
                        </div>
                        <div style={{ maxHeight: 280, overflowY: "auto", padding: "8px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                          {custApps.map((app: any) => (
                            <div key={app.appId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f5f5f5" }}>
                              <Checkbox
                                checked={selectedAppIds.includes(app.appId)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedAppIds((prev) => [...prev, app.appId]);
                                  else setSelectedAppIds((prev) => prev.filter((id) => id !== app.appId));
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>{app.appName}</span>
                              </Checkbox>
                              <span style={{ fontSize: 14, color: "#595959", fontWeight: 600 }}>
                                {fmt(app.appPrice)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Empty description="No active applications found for this customer" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </AntCard>

                  {/* Bundle Selection */}
                  {custBundles.length > 0 && (
                    <AntCard size="small" style={{ marginBottom: 16 }} title={<span><GiftOutlined style={{ marginRight: 6 }} />Select Enterprise Pricing to Invoice</span>}>
                      <div style={{ maxHeight: 200, overflowY: "auto", padding: "8px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                        {custBundles.map((bundle: any) => {
                          const price = Number(bundle.effectivePrice) || Number(bundle.bundlePrice) || 0;
                          return (
                            <div key={bundle.bundleId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f5f5f5" }}>
                              <Checkbox
                                checked={selectedBundleIds.includes(bundle.bundleId)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedBundleIds((prev) => [...prev, bundle.bundleId]);
                                  else setSelectedBundleIds((prev) => prev.filter((id) => id !== bundle.bundleId));
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>{bundle.bundleName}</span>
                                {bundle.bundleCode && <Tag style={{ marginLeft: 6, fontSize: 11 }}>{bundle.bundleCode}</Tag>}
                              </Checkbox>
                              <span style={{ fontSize: 14, color: "#595959", fontWeight: 600 }}>
                                {fmt(price)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </AntCard>
                  )}

                  {/* Discount & Notes */}
                  <AntCard size="small" style={{ marginBottom: 16 }} title="Discount & Notes">
                    <Row className="g-3">
                      <Col md="4">
                        <label className="form-label">Discount Type</label>
                        <Select
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="None"
                          value={discountType}
                          onChange={(val) => {
                            setDiscountType(val);
                            if (!val) setDiscountValue(undefined);
                          }}
                          options={[
                            { label: "Flat Amount", value: "Flat" },
                            { label: "Percentage (%)", value: "Percentage" },
                          ]}
                        />
                      </Col>
                      {discountType && (
                        <>
                          <Col md="4">
                            <label className="form-label">Discount Value</label>
                            <InputNumber
                              style={{ width: "100%" }}
                              value={discountValue}
                              onChange={(val) => setDiscountValue(val ?? undefined)}
                              min={0}
                              max={discountType === "Percentage" ? 20 : undefined}
                              precision={2}
                            />
                            {discountType === "Percentage" && (
                              <div style={{ fontSize: 12, color: "#8094ae", marginTop: 4 }}>Max 20% allowed</div>
                            )}
                          </Col>
                          <Col md="4">
                            <label className="form-label">Discount Reason</label>
                            <Input
                              value={discountReason}
                              onChange={(e) => setDiscountReason(e.target.value)}
                              placeholder="Reason for discount"
                            />
                          </Col>
                        </>
                      )}
                      <Col md="12">
                        <label className="form-label">Notes</label>
                        <Input.TextArea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={2}
                          maxLength={500}
                          placeholder="Optional notes for this invoice"
                        />
                      </Col>
                    </Row>
                  </AntCard>

                  {/* Advance Payment Adjustment */}
                  {custBillingConfig?.advancePaymentAllowed && advanceBalance > 0 && (
                    <AntCard
                      size="small"
                      style={{ marginBottom: 16, borderColor: "#722ed1" }}
                      title={<span><WalletOutlined style={{ marginRight: 6, color: "#722ed1" }} />Advance Payment Adjustment</span>}
                    >
                      <div style={{ marginBottom: 12, fontSize: 13 }}>
                        Available advance balance: <strong style={{ color: "#722ed1" }}>{fmt(advanceBalance)}</strong>
                      </div>
                      <Row className="g-3">
                        <Col md="4">
                          <Checkbox
                            checked={useAdvance}
                            onChange={(e) => {
                              setUseAdvance(e.target.checked);
                              if (e.target.checked) {
                                // Auto-fill: min of advance balance and net before advance
                                const autoAmount = Math.min(advanceBalance, netBeforeAdvance);
                                setAdvanceToAdjust(autoAmount > 0 ? Math.round(autoAmount * 100) / 100 : undefined);
                              } else {
                                setAdvanceToAdjust(undefined);
                              }
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>Deduct from Advance</span>
                          </Checkbox>
                        </Col>
                        {useAdvance && (
                          <Col md="4">
                            <label className="form-label">Amount to Deduct</label>
                            <InputNumber
                              style={{ width: "100%" }}
                              value={advanceToAdjust}
                              onChange={(val) => setAdvanceToAdjust(val ?? undefined)}
                              min={0.01}
                              max={advanceBalance}
                              precision={2}
                              prefix="₹"
                            />
                            <div style={{ fontSize: 11, color: "#8094ae", marginTop: 4 }}>
                              Max: {fmt(advanceBalance)}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </AntCard>
                  )}
                </>
              )}
            </Col>

            {/* Right Column: Live Breakup */}
            <Col lg="5">
              {customerId && !detailsLoading && (
                <div style={{ position: "sticky", top: 80 }}>
                  <AntCard
                    size="small"
                    title={<span style={{ fontSize: 15, fontWeight: 600 }}><DollarOutlined style={{ marginRight: 8 }} />Invoice Breakup</span>}
                    style={{ border: "2px solid #1890ff" }}
                  >
                    {totalItems === 0 ? (
                      <div style={{ textAlign: "center", padding: "30px 0", color: "#8094ae" }}>
                        Select applications or bundles to see the breakup
                      </div>
                    ) : (
                      <table style={{ width: "100%", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <th style={{ padding: "6px 0", fontWeight: 600, color: "#595959" }}>Item</th>
                            <th style={{ padding: "6px 0", fontWeight: 600, color: "#595959", textAlign: "right" }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedApps.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={2} style={{ fontWeight: 600, color: "#6576ff", paddingTop: 8, fontSize: 12 }}>
                                  <AppstoreOutlined style={{ marginRight: 4 }} />SaaS Pricing
                                </td>
                              </tr>
                              {selectedApps.map((app: any) => (
                                <tr key={app.appId}>
                                  <td style={{ paddingLeft: 16, color: "#595959", paddingBottom: 4 }}>{app.appName}</td>
                                  <td style={{ textAlign: "right", fontWeight: 500, paddingBottom: 4 }}>{fmt(app.appPrice)}</td>
                                </tr>
                              ))}
                            </>
                          )}

                          {selectedBundles.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={2} style={{ fontWeight: 600, color: "#722ed1", paddingTop: 8, fontSize: 12 }}>
                                  <GiftOutlined style={{ marginRight: 4 }} />Enterprise Pricing
                                </td>
                              </tr>
                              {selectedBundles.map((b: any) => (
                                <tr key={b.bundleId}>
                                  <td style={{ paddingLeft: 16, color: "#595959", paddingBottom: 4 }}>{b.bundleName}</td>
                                  <td style={{ textAlign: "right", fontWeight: 500, paddingBottom: 4 }}>
                                    {fmt(Number(b.effectivePrice) || Number(b.bundlePrice) || 0)}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}

                          <tr><td colSpan={2}><hr style={{ margin: "8px 0", borderColor: "#d9d9d9" }} /></td></tr>

                          <tr style={{ fontWeight: 600 }}>
                            <td style={{ paddingBottom: 4 }}>
                              Subtotal ({selectedAppIds.length} app{selectedAppIds.length !== 1 ? "s" : ""}
                              {selectedBundleIds.length > 0 ? ` + ${selectedBundleIds.length} bundle${selectedBundleIds.length !== 1 ? "s" : ""}` : ""})
                            </td>
                            <td style={{ textAlign: "right", paddingBottom: 4 }}>{fmt(subtotal)}</td>
                          </tr>

                          {couponDiscount > 0 && manualDiscount === 0 && (
                            <tr style={{ color: "#52c41a" }}>
                              <td style={{ paddingBottom: 4 }}>
                                Coupon Discount
                                <span style={{ fontSize: 11, marginLeft: 4 }}>
                                  ({custLinkedCoupon?.couponCode} — {custLinkedCoupon?.discountType === "Percent" ? `${custLinkedCoupon.discountPercent}%` : "Flat"})
                                </span>
                              </td>
                              <td style={{ textAlign: "right", paddingBottom: 4 }}>- {fmt(couponDiscount)}</td>
                            </tr>
                          )}

                          {manualDiscount > 0 && (
                            <tr style={{ color: "#fa8c16" }}>
                              <td style={{ paddingBottom: 4 }}>
                                Manual Discount
                                <span style={{ fontSize: 11, marginLeft: 4 }}>
                                  ({discountType === "Percentage" ? `${discountValue}%` : "Flat"})
                                </span>
                              </td>
                              <td style={{ textAlign: "right", paddingBottom: 4 }}>- {fmt(manualDiscount)}</td>
                            </tr>
                          )}

                          {manualDiscount > 0 && couponDiscount > 0 && (
                            <tr>
                              <td colSpan={2} style={{ fontSize: 11, color: "#fa8c16", paddingBottom: 4 }}>
                                Manual discount overrides coupon discount
                              </td>
                            </tr>
                          )}

                          {advanceDeduction > 0 && (
                            <>
                              <tr>
                                <td style={{ paddingBottom: 4 }}>Net Before Advance</td>
                                <td style={{ textAlign: "right", paddingBottom: 4 }}>{fmt(netBeforeAdvance)}</td>
                              </tr>
                              <tr style={{ color: "#722ed1" }}>
                                <td style={{ paddingBottom: 4 }}>
                                  <WalletOutlined style={{ marginRight: 4 }} />
                                  Advance Adjusted
                                </td>
                                <td style={{ textAlign: "right", paddingBottom: 4 }}>- {fmt(advanceDeduction)}</td>
                              </tr>
                            </>
                          )}

                          <tr>
                            <td style={{ paddingBottom: 4, fontWeight: 600 }}>Taxable Amount</td>
                            <td style={{ textAlign: "right", paddingBottom: 4, fontWeight: 600 }}>{fmt(taxableAmount)}</td>
                          </tr>

                          {taxRate > 0 && (
                            <tr>
                              <td style={{ paddingBottom: 4 }}>
                                Tax
                                <span style={{ fontSize: 11, marginLeft: 4 }}>
                                  ({custTaxConfig?.taxName} @ {taxRate}%)
                                </span>
                              </td>
                              <td style={{ textAlign: "right", paddingBottom: 4 }}>+ {fmt(taxAmount)}</td>
                            </tr>
                          )}

                          <tr><td colSpan={2}><hr style={{ margin: "8px 0", borderColor: "#1890ff" }} /></td></tr>

                          <tr style={{ fontWeight: 700, fontSize: 16 }}>
                            <td>Total</td>
                            <td style={{ textAlign: "right", color: "#1890ff" }}>
                              {custCurrencyCode ? `${custCurrencyCode} ` : ""}{fmt(totalAmount)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {totalItems > 0 && (
                      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                        <Button
                          color="primary"
                          onClick={handleSubmit}
                          disabled={saving || totalItems === 0}
                          style={{ flex: 1 }}
                        >
                          {saving ? <LoadingSpinner inline size="sm" /> : "Create Invoice"}
                        </Button>
                        <Button color="secondary" onClick={() => navigate("/sa-invoices")} disabled={saving}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </AntCard>
                </div>
              )}
            </Col>
          </Row>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default InvoiceCreate;
